import {
    createMeshBuffersForMaterialGroups,
    MeshBuffers,
    SceneNodes,
    getGroupPartNumber,
    isGroupPart,
    IMaterialData,
    keyForMeshMaterialData,
} from "@cm/template-nodes"
import {anyDifference, mapDifferent, objectFieldsDifferent} from "@template-editor/helpers/change-detection"
import {getThreeObjectPart, mathIsEqual, setThreeObjectPart, ThreeObject, updateTransform} from "@template-editor/helpers/three-object"
import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {Observable, of, tap} from "rxjs"
import {Three as THREE, ThreeMeshBvh} from "@cm/material-nodes/three"

THREE.BufferGeometry.prototype.computeBoundsTree = ThreeMeshBvh.computeBoundsTree
THREE.BufferGeometry.prototype.disposeBoundsTree = ThreeMeshBvh.disposeBoundsTree
THREE.Mesh.prototype.raycast = ThreeMeshBvh.acceleratedRaycast

THREE.BatchedMesh.prototype.computeBoundsTree = ThreeMeshBvh.computeBatchedBoundsTree
THREE.BatchedMesh.prototype.disposeBoundsTree = ThreeMeshBvh.disposeBatchedBoundsTree
THREE.BatchedMesh.prototype.raycast = ThreeMeshBvh.acceleratedRaycast

const bvhStrategy = ThreeMeshBvh.SAH

export function createBufferGeometry(meshBuffers: MeshBuffers): THREE.BufferGeometry {
    const {vertices, normals, uvs, indices} = meshBuffers

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3))
    geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3))
    for (let uvIdx = 0; uvIdx < uvs.length; uvIdx++) geometry.setAttribute(uvIdx === 0 ? "uv" : `uv${uvIdx}`, new THREE.BufferAttribute(uvs[uvIdx], 2))
    geometry.setIndex(new THREE.BufferAttribute(indices, 1))

    return geometry
}

export class ThreeMesh extends ThreeObject<SceneNodes.Mesh> {
    protected override renderObject: THREE.Group = new THREE.Group()
    wasDisposed = false
    geometryOrTransformChangedInLastSetup = false

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)
        setThreeObjectPart(this.renderObject, this)
    }

    override setup(sceneNode: SceneNodes.Mesh) {
        const {sceneManagerService, materialManagerService} = this.threeSceneManagerService

        const meshHash = sceneNode.completeMeshData.reified.contentHash

        this.geometryOrTransformChangedInLastSetup = false

        const setupDeferredMaterialMaterial = (
            materialIndex: number,
            mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>,
            deferredMaterial: [Observable<THREE.Material>, IMaterialData],
            callback?: (material: THREE.Material) => void,
        ) => {
            const [material, materialData] = deferredMaterial
            sceneManagerService.addTask({
                description: `setupMaterial(${meshHash}-${materialIndex}, ${keyForMeshMaterialData(materialData, sceneNode.meshRenderSettings)})`,
                task: material.pipe(
                    tap((material) => {
                        if (this.wasDisposed) {
                            materialManagerService.releaseMaterial(material)
                            return
                        }

                        materialManagerService.releaseMaterial(mesh.material)
                        mesh.material = material

                        if (callback) callback(material)

                        this.threeSceneManagerService.exchangedShadowMapMeshTexture(mesh)

                        this.onAsyncUpdate()
                    }),
                ),
                critical: true,
            })
        }

        const getMaterial = (materialIndex: number): [THREE.Material, [Observable<THREE.Material>, IMaterialData] | null] => {
            const materialData = sceneNode.materialMap.get(materialIndex)
            if (materialData) {
                const [defaultMaterial, deferredMaterial] = materialManagerService.acquireMaterial(materialData, sceneNode.meshRenderSettings, materialIndex)
                if (!deferredMaterial) return [defaultMaterial, null]
                return [defaultMaterial, [deferredMaterial, materialData]]
            } else return [materialManagerService.acquireDefaultMaterial(undefined, materialIndex), null]
        }

        const notifyCachedMaterial = (materialIndex: number) => {
            const materialData = sceneNode.materialMap.get(materialIndex)
            if (materialData)
                sceneManagerService.addTask({
                    description: `setupCachedMaterial(${meshHash}-${materialIndex}, ${keyForMeshMaterialData(materialData, sceneNode.meshRenderSettings)})`,
                    task: of(null),
                    critical: true,
                })
        }

        const key = (materialData: IMaterialData | null | undefined, mesh: SceneNodes.Mesh | undefined) => {
            if (materialData === null) return null
            if (materialData === undefined) return undefined
            return keyForMeshMaterialData(materialData, mesh?.meshRenderSettings)
        }

        let subMeshesUpdated = false
        return anyDifference([
            objectFieldsDifferent(
                sceneNode.completeMeshData.reified,
                this.parameters?.completeMeshData.reified,
                ["uvs", "materialGroups", "vertices", "normals", "faceIDs", "indices"],
                undefined,
                (reified) => {
                    const subMeshes = this.getSubMeshes()
                    const meshBuffersByGroup = createMeshBuffersForMaterialGroups(reified).filter((x) => x.indices.length > 0)

                    for (const meshBuffer of meshBuffersByGroup) {
                        const bufferGeometry = createBufferGeometry(meshBuffer)
                        const {materialIndex} = meshBuffer

                        if (this.threeSceneManagerService.$useBvh()) bufferGeometry.computeBoundsTree({strategy: bvhStrategy})

                        const subMesh = subMeshes.find((subMesh) => materialIndex === subMesh.materialIndex)?.subMesh

                        if (subMesh) {
                            subMesh.geometry.disposeBoundsTree()
                            subMesh.geometry.dispose()
                            if (this.threeSceneManagerService.removeShadowMapMesh(subMesh, true)) {
                                this.threeSceneManagerService.detachShadowMapMesh(subMesh)
                                this.threeSceneManagerService.addShadowMapMesh(subMesh)
                            }
                            subMesh.geometry = bufferGeometry
                        } else {
                            const mesh = new THREE.Mesh(bufferGeometry)
                            setThreeObjectPart(mesh, this, `group${materialIndex}`)
                            this.renderObject.add(mesh)
                        }
                    }

                    for (const subMesh of subMeshes) {
                        if (!meshBuffersByGroup.find((meshBuffer) => meshBuffer.materialIndex === subMesh.materialIndex)) {
                            this.disposeSubMesh(subMesh.subMesh)
                        }
                    }

                    subMeshesUpdated = true
                    this.geometryOrTransformChangedInLastSetup = true
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform}) => {
                    updateTransform(transform, this.renderObject)
                    this.geometryOrTransformChangedInLastSetup = true
                },
            ),
            (() => {
                if (
                    mapDifferent(sceneNode.materialMap, this.parameters?.materialMap, (valueA, valueB) => {
                        return valueA === valueB || key(valueA, sceneNode) === key(valueB, this.parameters)
                    }) ||
                    objectFieldsDifferent(
                        sceneNode.meshRenderSettings,
                        this.parameters?.meshRenderSettings,
                        [
                            "displacementUvChannel",
                            "displacementMin",
                            "displacementMax",
                            "displacementNormalStrength",
                            "displacementNormalSmoothness",
                            "displacementNormalOriginalResolution",
                        ],
                        undefined,
                    ) ||
                    sceneNode.meshRenderSettings.displacementImage?.imageNode.hash !== this.parameters?.meshRenderSettings?.displacementImage?.imageNode.hash ||
                    objectFieldsDifferent(sceneNode.completeMeshData.reified, this.parameters?.completeMeshData.reified, ["materialGroups"], undefined)
                ) {
                    const {materialManagerService} = this.threeSceneManagerService

                    const subMeshes = this.getSubMeshes()

                    let allMaterialsDeferred = true
                    for (const [materialIndex, materialData] of sceneNode.materialMap) {
                        const oldMaterialData = this.parameters?.materialMap.get(materialIndex)
                        const oldMaterialGroupsIndices = this.parameters?.completeMeshData.reified.materialGroups.map((group) => group.materialIndex)

                        if (key(materialData, sceneNode) === key(oldMaterialData, this.parameters) && oldMaterialGroupsIndices?.includes(materialIndex))
                            continue

                        const mesh = subMeshes.find((subMesh) => subMesh.materialIndex === materialIndex)?.subMesh
                        if (!mesh) continue

                        const [material, deferredMaterial] = getMaterial(materialIndex)

                        if (!deferredMaterial) {
                            materialManagerService.releaseMaterial(mesh.material)
                            mesh.material = material
                            this.threeSceneManagerService.exchangedShadowMapMeshTexture(mesh)
                            allMaterialsDeferred = false
                            notifyCachedMaterial(materialIndex)
                        } else {
                            if (mesh.material instanceof THREE.MeshBasicMaterial && subMeshesUpdated) {
                                materialManagerService.releaseMaterial(mesh.material)
                                mesh.material = material
                                this.threeSceneManagerService.exchangedShadowMapMeshTexture(mesh)
                                allMaterialsDeferred = false
                            } else {
                                materialManagerService.releaseMaterial(material)
                            }

                            setupDeferredMaterialMaterial(materialIndex, mesh, deferredMaterial)
                        }
                    }

                    return !allMaterialsDeferred
                } else return false
            })(),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["receiveRealtimeShadows"],
                (a, b) => a === b && subMeshesUpdated === false,
                ({receiveRealtimeShadows}) => {
                    this.getSubMeshes().forEach(({subMesh}) => {
                        subMesh.receiveShadow = receiveRealtimeShadows
                        if (receiveRealtimeShadows) this.threeSceneManagerService.addShadowMapMesh(subMesh)
                        else this.threeSceneManagerService.removeShadowMapMesh(subMesh, false)
                    })
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["castRealtimeShadows"],
                (a, b) => a === b && subMeshesUpdated === false,
                ({castRealtimeShadows}) => {
                    this.getSubMeshes().forEach(({subMesh}) => {
                        subMesh.castShadow = castRealtimeShadows
                    })
                    this.threeSceneManagerService.requestShadowMapUpdate()
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["isDecal"],
                (a, b) => a === b && subMeshesUpdated === false,
                ({isDecal}) => {
                    this.getSubMeshes().forEach(({subMesh}) => {
                        subMesh.renderOrder = isDecal ? 1 : 0
                    })
                },
            ),
        ])
    }

    getSubMesh(materialIndex: number) {
        for (const child of this.renderObject.children) {
            if (child instanceof THREE.Mesh) {
                const mesh = child as THREE.Mesh<THREE.BufferGeometry, THREE.Material>
                const threeObjectPart = getThreeObjectPart(mesh)

                if (threeObjectPart && threeObjectPart.part === `group${materialIndex}`) return mesh
            }
        }

        throw new Error(`SubMesh with materialIndex ${materialIndex} not found`)
    }

    getSubMeshes() {
        const result: {subMesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>; materialIndex: number}[] = []

        for (const child of this.renderObject.children) {
            if (child instanceof THREE.Mesh) {
                const mesh = child as THREE.Mesh<THREE.BufferGeometry, THREE.Material>
                const threeObjectPart = getThreeObjectPart(mesh)

                if (threeObjectPart && isGroupPart(threeObjectPart.part)) {
                    const materialIndex = getGroupPartNumber(threeObjectPart.part)
                    result.push({subMesh: mesh, materialIndex})
                }
            }
        }

        return result
    }

    hasBvh() {
        const subMeshes = this.getSubMeshes()
        if (subMeshes.length === 0) return false
        return subMeshes.every(({subMesh}) => subMesh.geometry.boundsTree !== undefined)
    }

    computeBvh() {
        const subMeshes = this.getSubMeshes()
        subMeshes.forEach(({subMesh}) => {
            if (subMesh.geometry.boundsTree !== undefined) return
            subMesh.geometry.computeBoundsTree({strategy: bvhStrategy})
        })
    }

    private disposeSubMesh(subMesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>) {
        const {materialManagerService} = this.threeSceneManagerService

        this.threeSceneManagerService.removeShadowMapMesh(subMesh, true)
        subMesh.geometry.disposeBoundsTree()
        subMesh.geometry.dispose()
        materialManagerService.releaseMaterial(subMesh.material)

        this.renderObject.remove(subMesh)
    }

    override dispose(final: boolean) {
        this.getSubMeshes().forEach(({subMesh}) => {
            this.disposeSubMesh(subMesh)
        })

        if (final) this.wasDisposed = true
    }

    ensureBoundsTree() {
        this.getSubMeshes().forEach(({subMesh}) => {
            if (subMesh.geometry.boundsTree === null) subMesh.geometry.computeBoundsTree({strategy: ThreeMeshBvh.SAH})
        })
    }
}
